import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkEditTags } from 'src/components/common/BulkActions/BulkEditTagsAction';
import { useBulkEditTagsPermissions } from 'src/components/common/BulkActions/BulkEditTagsAction/useBulkEditTagsPermissions';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import {
  BulkEditTagAction,
  BulkEditTagDialog,
} from 'src/dialogs/BulkEdits/BulkEditTagDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  CommandOptionType,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_UPDATE_TAGS_UPDATE_KEY } from '../../PurchaseActionDropdown.constants';

export const useBulkEditTagsAction = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  shouldRegister?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasEditTagsPermissions } = useBulkEditTagsPermissions({
    entityType: ActionOutboxEntityType.Purchase,
  });

  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = groupId
    ? `purchaseTags-event-${groupId}}`
    : PURCHASE_BULK_UPDATE_TAGS_UPDATE_KEY;
  const { refreshData } = usePurchaseDataContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkEditTagDialog,
    closeDialog: closeBulkEditTagDialog,
  } = useDialog(DialogId.BulkEditTags, BulkEditTagDialog);

  const onSubmitEditTags = useCallback(
    async (
      action: BulkEditTagAction,
      tagKey: string | null,
      tagValue?: string | undefined,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            ).bulkUpdateTagsPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateTagsPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (tagKey) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const succeeded =
              action === BulkEditTagAction.Update
                ? await client.bulkUpdateTags(
                    preview!.preview,
                    tagKey,
                    tagValue,
                    updateKey,
                    supportBackgroundProcess
                  )
                : await client.bulkRemoveTags(
                    preview!.preview,
                    tagKey,
                    updateKey,
                    supportBackgroundProcess
                  );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on details modal
                // we force re-loading the entity and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshData(true);
                await refreshExpandedListItems();
              }

              closeBulkEditTagDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitEditTags', error, {
              trackErrorData: { tagKey, tagValue, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQuery,
      showErrorDialog,
      updateKey,
      closeBulkEditTagDialog,
      setActivePosEntity,
      refreshData,
      refreshExpandedListItems,
    ]
  );

  const onChangeTagClick = useCallback(
    (e: React.MouseEvent, editTagAction: BulkEditTagAction) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkEditTagDialog({
        updateKey: updateKey,
        action: editTagAction,
        onOkay: onSubmitEditTags,
        entityType: ActionOutboxEntityType.Purchase,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditTagDialog,
      });
    },
    [
      closeBulkEditTagDialog,
      onSubmitEditTags,
      openBulkEditTagDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const openAddTagDialogStableRef = useEventCallback(() => {
    openBulkEditTagDialog({
      updateKey: updateKey,
      action: BulkEditTagAction.Update,
      onOkay: onSubmitEditTags,
      entityType: ActionOutboxEntityType.Purchase,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditTagDialog,
    });
  });

  const openRemoveTagDialogStableRef = useEventCallback(() => {
    openBulkEditTagDialog({
      updateKey: updateKey,
      action: BulkEditTagAction.Remove,
      onOkay: onSubmitEditTags,
      entityType: ActionOutboxEntityType.Purchase,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditTagDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkEditAddTag,
        onSelect: openAddTagDialogStableRef,
      },
      disabled: !hasEditTagsPermissions || !shouldRegister,
    });

    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkEditRemoveTag,
        onSelect: openRemoveTagDialogStableRef,
      },
      disabled: !hasEditTagsPermissions || !shouldRegister,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkEditAddTag,
        disabled: !hasEditTagsPermissions || !shouldRegister,
      });
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkEditRemoveTag,
        disabled: !hasEditTagsPermissions || !shouldRegister,
      });
    };
  }, [
    hasEditTagsPermissions,
    shouldRegister,
    openAddTagDialogStableRef,
    openRemoveTagDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasEditTagsPermissions) {
      return [];
    }

    return [
      {
        title: ContentId.UpdateTagValues,
        onClick: (e: React.MouseEvent) => {
          onChangeTagClick(e, BulkEditTagAction.Update);
        },
        disabled: isLoading,
      },
      {
        title: ContentId.RemoveTagValues,
        onClick: (e: React.MouseEvent) => {
          onChangeTagClick(e, BulkEditTagAction.Remove);
        },
        disabled: isLoading,
      },
    ];
  }, [hasEditTagsPermissions, isLoading, onChangeTagClick]);

  return {
    dropDown: (
      <LaunchBulkEditTags
        key="EditTags"
        onClick={onChangeTagClick}
        disabled={isLoading}
        entityType={ActionOutboxEntityType.Purchase}
      />
    ),
    actionItems,
  };
};
